import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject, Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { gsap } from 'gsap/all';
import { tap } from 'rxjs/operators';

import { TEMPLATE_PREFIX } from '../../../../environments/locale-config';
import { KHPoint } from '../../../../LearningWorlds/pixi-puzzle-framework/PuzzleEngine/KHPoint';
import { IModalService, MODAL_SERVICE_TOKEN } from '../../../../services/IModalService';
import { IMusicService, MUSIC_SERVICE_TOKEN } from '../../../../services/IMusicService';
import { IQuestionEventsService, QUESTION_EVENTS_SERVICE_TOKEN } from '../../../../services/IQuestionEvents';
import { IStudentMusicService, STUDENT_MUSIC_SERVICE_TOKEN } from '../../../../services/IStudentMusicService';
import { AlgebraTileAddComponent } from '../../../modals';

import { AlgebaTileDraggable, tileDataMap, TileType } from './algebaTileDraggable';

const MIN_SCALE = 1;

@UntilDestroy()
@Component({
  selector: 'kh-manip-algebra-tiles',
  styleUrls: ['./manip-algebra-tiles.component.less'],
  templateUrl: TEMPLATE_PREFIX + 'manip-algebra-tiles.component.html',
  standalone: false
})
export class ManipAlgebraTilesComponent implements OnInit, AfterViewInit {
  @ViewChild('container') public parentContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('canvas') public canvasEl?: ElementRef<HTMLDivElement>;

  public questionClosed = false;
  public randomId = Math.floor(Math.random() * 9999);
  public initValue?: string;

  @ViewChild('add') add: ElementRef;

  @Input() public displaySummary = false;
  @Output() public expressionChange = new EventEmitter<string>();
  @Output() public submitAnswer = new EventEmitter<string>();

  private expressionValue = '';
  private algetileCanvasWidth = 300;
  private algetileCanvasHeight = 210;
  private allTiles = ['-x^2', 'x^2', '-x', 'x', '-1', '1'];
  private internalExpression = '``';
  private canvas!: HTMLDivElement;
  private musicService!: IMusicService | IStudentMusicService;
  public isManipChanged = false;
  public tiles: AlgebaTileDraggable[] = [];
  private selectedTile: AlgebaTileDraggable | undefined;

  public hideStudentManipControlBar = this.questionEventsService.hideStudentManipControlBar.asObservable();

  public constructor(
    @Inject(MODAL_SERVICE_TOKEN) private readonly modalService: IModalService,
    private readonly hostElement: ElementRef,
    @Inject(QUESTION_EVENTS_SERVICE_TOKEN) private readonly questionEventsService: IQuestionEventsService,
    private readonly renderer: Renderer2,
    @Inject(STUDENT_MUSIC_SERVICE_TOKEN) private readonly studentMusicService: IStudentMusicService,
    @Inject(MUSIC_SERVICE_TOKEN) private readonly sharedMusicService: IMusicService,
    private readonly route: ActivatedRoute) {
  }

  @HostListener('window:resize')
  public onResize() {
    this.resizeWorks();
  }

  public resizeWorks(): void {
    const elemParentNode = this.hostElement.nativeElement.parentNode;
    (this.hostElement.nativeElement as HTMLDivElement).hidden = true;
    this.renderer.setStyle(this.hostElement.nativeElement, 'display', 'none');

    const scale = this.calculateScale(elemParentNode.clientWidth);

    this.renderer.setStyle(this.hostElement.nativeElement, 'display', 'flex');
    (this.hostElement.nativeElement as HTMLDivElement).hidden = false;
    this.canvas.style.width = `${this.algetileCanvasWidth * scale}px`;
    this.canvas.style.height = `${this.algetileCanvasHeight * scale}px`;
  }

  private calculateScale(clientWidth: number): number {
    const scale = Math.min(MIN_SCALE, clientWidth / this.algetileCanvasWidth);

    return scale || MIN_SCALE;
  }

  public get selectedObject(): AlgebaTileDraggable | undefined {
    return this.selectedTile;
  }

  public get expression(): string {
    return this.expressionValue;
  }

  @Input()
  public set expression(value: string) {
    this.initValue = value;
  }

  public ngOnInit(): void {
    this.questionEventsService.hideAllInputForManip.next(true);
    this.questionEventsService.questionClosed
      .pipe(
        tap(() => {
          this.questionClosed = true;
        }),
        untilDestroyed(this)
      )
      .subscribe();

    // HACK: Temporary solution. Should be refactored to receive component mode (isPreview: true/false) as an input.
    this.musicService = this.route.snapshot.url.find(segment => segment.path === 'student') ? this.studentMusicService : this.sharedMusicService;
  }

  public ngAfterViewInit(): void {
    if (this.canvasEl?.nativeElement) {
      this.canvas = this.canvasEl.nativeElement;
    }

    // Glow animation when the secondary buttons are not engaged for more than 5 seconds.
    setTimeout(() => {
      if (!this.isManipChanged) {
        this.add?.nativeElement.classList.add('is-active');
      }
    }, 5000);

    this.resizeWorks();

    if (this.initValue) {
      this.onExpressionSet(this.initValue);
    }
  }

  public updateExpression(): void {
    this.internalExpression = this.getExpression();
    this.expressionValue = this.internalExpression;
  }

  public rotate(): void {
    this.isManipChanged = true;
    // remove glow animation
    this.add?.nativeElement.classList.remove('is-active');

    if (this.selectedTile) {
      this.selectedTile.rotate();
    }
  }

  public toggleNegative(): void {
    this.isManipChanged = true;
    // remove glow animation
    this.add?.nativeElement.classList.remove('is-active');

    if (this.selectedTile) {
      this.selectedTile.changeSign();
    }

    this.updateExpression();
  }

  public delete(): void {
    this.isManipChanged = true;
    // remove glow animation
    this.add?.nativeElement.classList.remove('is-active');
  }

  public duplicate(): void {
    this.isManipChanged = true;
    // remove glow animation
    this.add?.nativeElement.classList.remove('is-active');

    // duplicate with a 15x15px offset
    if (this.selectedTile) {
      const pos = this.selectedTile.getPosition();

      this.addTile(this.selectedTile?.tileType.toString(), { x: pos.x + 15, y: pos.y + 15 });
    }

    return;
  }

  public openAddTileModal(): void {
    this.isManipChanged = true;
    // remove glow animation
    this.add?.nativeElement.classList.remove('is-active');
    this.musicService.playSound('pop.mp3');

    const modal = this.modalService.displayModal<AlgebraTileAddComponent>(AlgebraTileAddComponent, { allTiles: this.allTiles }, 'md', true);

    modal.content?.add.pipe(
      tap((tile: string) => { this.addTile(tile); }),
      untilDestroyed(this)
    ).subscribe();
  }

  public submit(expression: string): void {
    this.submitAnswer.emit(expression);
  }

  private onExpressionSet(expression: string): void {
    const isExternallyUpdated = expression !== null && (this.internalExpression !== expression);
    if (!isExternallyUpdated || !(expression.length > 2 && expression[0] === '`' && expression[expression.length - 1] === '`')) {
      return;
    }

    let tempExpression = expression
      .split(' ')
      .join('')
      .split('`')
      .join('');

    for (let i = 0; i < this.allTiles.length; i++) {
      const itemLocation = tempExpression.indexOf(this.allTiles[i]);
      if (itemLocation >= 0) { // Exponent exists
        let NumInstances = 1;

        let j = 1;
        while (itemLocation >= j && !isNaN(+tempExpression[itemLocation - j])) {
          NumInstances = parseInt(tempExpression.substring(itemLocation - j, itemLocation), 10);
          j++;
        }
        tempExpression = tempExpression.replace(NumInstances + this.allTiles[i], '').replace(this.allTiles[i], '');
        for (j = 0; j < NumInstances; j++) {
          this.addTile(this.allTiles[i]);
        }
      }
    }
  }

  private addTile(tile: string, position?: KHPoint): void {
    this.musicService.playSound('pop.mp3');

    // add new tile
    const tileObject = new AlgebaTileDraggable(this.canvas,
      this.getEnumFromTileDataMap(tile),
      position || this.getRandomStartPosition(),
      this.onTileDrag.bind(this),
      this.onDragStop.bind(this)
    );

    tileObject.draggableDiv.addEventListener('mousedown', () => {
      this.selectedTile = tileObject;
    });

    this.tiles.push(tileObject);

    this.selectedTile = tileObject;

    this.updateExpression();
  }

  private onDragStop(): void {
    this.removeTilesOutOfBounds();
    this.removeIntersectingTiles();
    this.updateExpression();
  }

  private onTileDrag(): void {
    if (!this.selectedTile) {
      return;
    }

    let hasIntersection = false;

    for (const tile of this.tiles) {
      if (!hasIntersection && tile !== this.selectedTile && tile.canIntersect(this.selectedTile)) {
        tile.setIntersecting(true);
        hasIntersection = true;
      } else {
        tile.setIntersecting(false);
      }
    }

    this.selectedTile.setIntersecting(hasIntersection);
  }

  private getEnumFromTileDataMap(tile: string): TileType {
    for (const [key, value] of Object.entries(tileDataMap)) {
      if (value.value === tile) {
        return key as TileType;
      }
    }

    throw new Error(`Tile ${tile} not found in tileDataMap`);
  }

  private getRandomStartPosition(): KHPoint {
    return {
      x: Math.random() * (this.canvas.clientWidth - 50),
      y: Math.random() * (this.canvas.clientHeight - 50)
    };
  }

  private getExpression(): string {
    const numTiles = [0, 0, 0, 0, 0, 0];

    this.tiles.forEach((tile: AlgebaTileDraggable) => {
      numTiles[this.allTiles.indexOf(tile.tileType)]++;
    });

    let expression = '';

    for (let i = 0; i < numTiles.length; i++) {
      if (numTiles[i] >= 1) {
        if (this.allTiles[i][0] === '-') {
          expression += ' - ' +
          (numTiles[i] === 1 ? '' : numTiles[i]) +
          (numTiles[i] > 1 && this.allTiles[i] === '-1' ? '' : this.allTiles[i].substring(1, 256));
        } else {
          expression += ' + ' +
          (numTiles[i] === 1 ? '' : numTiles[i]) +
          (numTiles[i] > 1 && this.allTiles[i] === '1' ? '' : this.allTiles[i]);
        }
      }
    }

    if (expression.length > 0) {
      if (expression[1] === '-') {
        expression = '-' + expression.substring(3, 1027);
      } else {
        expression = expression.substring(3, 1027);
      }
    }

    return expression === '' ? expression : '`' + expression + '`';
  }

  private poofAnimation(position: KHPoint): void {
    const img = new Image();
    const parentContainer = this.parentContainer.nativeElement;
    img.src = 'images/poof.png';
    img.style.position = 'absolute';
    img.style.float = 'left';
    parentContainer.appendChild(img);
    img.style.top = `${position.y}px`;
    img.style.left = `${position.x}px`;
    img.style.zIndex = '1000';

    gsap.to(img, {
      opacity: 0,
      duration: 0.6,
      y: '-=20',
      ease: 'power3.in',
      onComplete: () => {
        parentContainer.removeChild(img);
      }
    });
  }

  private isTileOutOfBounds(tile: AlgebaTileDraggable): boolean {
    const tileRect = tile.getRect();
    const containerRect = this.canvas.getBoundingClientRect();

    // Check if tile is completely outside in any direction
    return (
      tileRect.right < containerRect.left // Completely to the left
      || tileRect.left > containerRect.right // Completely to the right
      || tileRect.bottom < containerRect.top // Completely above
      || tileRect.top > containerRect.bottom // Completely below
    );
  }

  private removeTilesOutOfBounds(): void {
    const objectsToDelete: AlgebaTileDraggable[] = [];

    for (const tile of this.tiles) {
      if (this.isTileOutOfBounds(tile)) {
        objectsToDelete.push(tile);
      }
    }

    for (const tile of objectsToDelete) {
      if (this.selectedTile === tile) {
        this.selectedTile = undefined;
      }

      this.tiles.splice(this.tiles.indexOf(tile), 1);
      tile.delete();
    }
  }

  private removeIntersectingTiles(): void {
    const objectsToDelete: AlgebaTileDraggable[] = [];

    for (const tile of this.tiles) {
      if (tile.hasIntersection) {
        tile.delete(true);
        objectsToDelete.push(tile);

        if (this.selectedTile !== tile) {
          const position = tile.getRect();
          this.poofAnimation({ x: position.left, y: position.top });
        }
      }
    }

    for (const tile of objectsToDelete) {
      this.tiles.splice(this.tiles.indexOf(tile), 1);

      if (this.selectedTile === tile) {
        this.selectedTile = undefined;
      }
    }
  }
}
